import React from 'react'
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'

import { INTERAX_COLORS } from '../../../../constants/colors'
import { COMPONENT_DIMENSIONS, LAYOUT_SPACING, Z_INDEX } from '../../../../constants/layout'

export interface OnboardingStepProgressItem {
  id: string
  label: string
}

interface OnboardingStepProgressProps {
  steps: OnboardingStepProgressItem[]
  currentStep: number
  /** Localized "Step X of Y — <title>" template (rendered on mobile). */
  mobileStepLabel: string
}

/**
 * Horizontal numbered step indicator with connector lines — visually matches
 * `FlowTopComponent` used by the reflection wizard, so onboarding feels native.
 * Pure presentation; the parent owns step state.
 */
export const OnboardingStepProgress: React.FC<OnboardingStepProgressProps> = ({
  steps,
  currentStep,
  mobileStepLabel
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const stepSize = isMobile ? 28 : COMPONENT_DIMENSIONS.FLOW_STEP_SIZE
  const stepHalf = stepSize / 2

  const getStepColors = (isCompleted: boolean, isCurrent: boolean) => {
    if (isCompleted || isCurrent) {
      return { backgroundColor: INTERAX_COLORS.sun, color: INTERAX_COLORS.offBlack }
    }
    return { backgroundColor: INTERAX_COLORS.thunderGrey, color: INTERAX_COLORS.white }
  }

  return (
    <Box sx={{ mb: `${LAYOUT_SPACING.FLOW_HEADER_MARGIN}px` }}>
      <Box
        sx={{
          maxWidth: '72rem',
          mx: 'auto',
          position: 'relative',
          width: '100%'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            maxWidth: { xs: '100%', sm: '40rem', md: '48rem' },
            mx: 'auto',
            position: 'relative'
          }}
        >
          {steps.map((step, index) => {
            const isCompleted = index < currentStep
            const isCurrent = index === currentStep
            const isPast = index < currentStep
            const colors = getStepColors(isCompleted, isCurrent)
            const textColor = isPast || isCurrent ? INTERAX_COLORS.white : INTERAX_COLORS.white70

            return (
              <Box
                key={step.id}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  flex: 1,
                  position: 'relative',
                  minWidth: 0
                }}
              >
                <Box
                  sx={{
                    width: stepSize,
                    height: stepSize,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: isMobile ? 11 : 18,
                    fontWeight: 'bold',
                    mb: 1.5,
                    flexShrink: 0,
                    transition: 'all 0.3s',
                    position: 'relative',
                    zIndex: Z_INDEX.FLOW_STEP_BUTTON,
                    backgroundColor: colors.backgroundColor,
                    color: colors.color,
                    ...(isCompleted || isCurrent ? { boxShadow: `0 0 0 4px ${INTERAX_COLORS.sun}30` } : {})
                  }}
                >
                  {index + 1}
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    display: { xs: 'none', sm: 'flex' },
                    textAlign: 'center',
                    lineHeight: 1.25,
                    minHeight: '3rem',
                    alignItems: 'center',
                    justifyContent: 'center',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    maxWidth: { sm: '7.5rem', md: '8rem' },
                    px: 0.5,
                    fontWeight: isPast || isCurrent ? 500 : 400,
                    color: textColor
                  }}
                >
                  {step.label}
                </Typography>

                {index < steps.length - 1 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '50%',
                      transform: 'translateY(-50%)',
                      height: `${COMPONENT_DIMENSIONS.BORDER_THIN}px`,
                      top: `${stepHalf}px`,
                      zIndex: Z_INDEX.FLOW_BACKGROUND_LINE,
                      width: `calc(100% - ${stepSize}px)`,
                      ml: `${stepHalf}px`,
                      backgroundColor: isCompleted ? INTERAX_COLORS.sun : INTERAX_COLORS.thunderGrey
                    }}
                  />
                )}
              </Box>
            )
          })}
        </Box>

        {/* Mobile-only summary indicator */}
        <Typography
          variant="caption"
          sx={{
            display: { xs: 'block', sm: 'none' },
            textAlign: 'center',
            mt: 1,
            px: 1,
            color: INTERAX_COLORS.thunderGrey
          }}
        >
          {mobileStepLabel}
        </Typography>
      </Box>
    </Box>
  )
}
